import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet-async";
import { Loader2, ArrowLeft } from "lucide-react";
import { publicApi, publicError } from "@/lib/publicApi";
import { Badge } from "@/components/ui/badge";
import CTASection from "@/components/public/CTASection";
import SEO from "@/components/public/SEO";
import NotFound from "@/pages/NotFound";

export default function BlogDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        setPost(null);
        setError(null);
        publicApi.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch((e) => setError(e?.response?.status === 404 ? "notfound" : publicError(e)));
    }, [slug]);

    if (error === "notfound") return <NotFound message="That article doesn't exist." backTo="/blog" backLabel="Back to Blog" />;
    if (!post) return <div className="min-h-[60vh] grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

    const date = post.published_date ? new Date(post.published_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
    const origin =
        process.env.REACT_APP_SITE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "");
    const canonical = `${origin}/blog/${post.slug}`;
    const ogImg = post.og_image || post.featured_image || "/og-default.png";
    const absImg = /^https?:\/\//i.test(ogImg) ? ogImg : `${origin}${ogImg.startsWith("/") ? "" : "/"}${ogImg}`;
    const blogJsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.short_summary,
        image: absImg,
        datePublished: post.published_date,
        author: { "@type": "Organization", name: post.author || "Omega IT Ltd" },
        publisher: {
            "@type": "Organization",
            name: "Omega IT Ltd",
            logo: { "@type": "ImageObject", url: `${origin}/og-default.png` },
        },
        mainEntityOfPage: canonical,
    };

    return (
        <div data-testid={`blog-detail-${post.slug}`}>
            <SEO
                title={post.seo_title || post.title}
                description={post.seo_description || post.short_summary}
                canonical={canonical}
                ogType="article"
                ogImage={post.og_image || post.featured_image}
                jsonLd={blogJsonLd}
            />
            <Helmet>
                {post.published_date && (
                    <meta property="article:published_time" content={post.published_date} />
                )}
                <meta property="article:author" content={post.author || "Omega IT Ltd"} />
            </Helmet>
            <article>
                <section className="bg-white border-b border-slate-100">
                    <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
                        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-950"><ArrowLeft className="w-4 h-4" /> All articles</Link>
                        <div className="mt-6 flex items-center gap-2">
                            <Badge variant="outline" className="border-slate-200 text-slate-700">{post.category}</Badge>
                            {date && <span className="text-xs text-slate-500">{date}</span>}
                        </div>
                        <h1 className="font-display font-black tracking-tight text-slate-950 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">{post.title}</h1>
                        <p className="text-base sm:text-lg text-slate-600 mt-5 leading-relaxed">{post.short_summary}</p>
                        <div className="text-xs text-slate-500 mt-5">By {post.author || "Omega IT Ltd"}</div>
                    </div>
                </section>
                {post.featured_image && (
                    <div className="px-5 sm:px-8 lg:px-12 mt-10">
                        <img src={post.featured_image} alt="" className="max-w-4xl mx-auto rounded-2xl border border-slate-200" />
                    </div>
                )}
                <section className="px-5 sm:px-8 lg:px-12 py-16">
                    <div className="prose prose-slate max-w-3xl mx-auto prose-headings:font-display prose-headings:font-bold prose-headings:tracking-tight prose-h3:text-xl prose-p:leading-relaxed prose-li:leading-relaxed">
                        <ReactMarkdown>{post.full_content || ""}</ReactMarkdown>
                    </div>
                </section>
            </article>
            <CTASection title="Want this for your business?" description="If you read something here you'd like to make happen for your business, let's chat." />
        </div>
    );
}
